import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useDebounce } from 'usehooks-ts';
import { useEffect, useState } from 'react';
import { RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type Props<T> = {
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  label: string;
  loading?: boolean;
  options: T[];
  debounceInMS?: number;
  idProperty: string;
  labelProperty: string;
  noOptionsText?: string;
  onSelected?: (option?: T | null) => void;
  onQueryChange?: (query: string) => void;
  ref?: RefCallBack;
  showOptionsOnFocus?: boolean;
  value?: T;
  variant?: 'outlined' | 'standard' | 'filled';
};

export default function Search<T>(props: Props<T>) {
  const {
    debounceInMS = 500,
    disabled,
    error,
    fullWidth = false,
    helperText,
    label,
    labelProperty,
    loading,
    idProperty,
    noOptionsText,
    onSelected,
    onQueryChange,
    options = [],
    ref,
    showOptionsOnFocus,
    value,
    variant = 'outlined'
  } = props;
  const { t } = useTranslation('common');
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, debounceInMS);

  const handleSelect = (option: T | null | undefined) => {
    onSelected && onSelected(option);
    setQuery('');
  };

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      onQueryChange && onQueryChange(debouncedQuery);
    }
  }, [debouncedQuery]);

  return (
    <Autocomplete<T>
      defaultValue={value}
      autoHighlight
      options={
        showOptionsOnFocus || (debouncedQuery && !loading)
          ? options.sort((a, b) => {
              return a[labelProperty] > b[labelProperty] ? 1 : -1;
            })
          : []
      }
      noOptionsText={noOptionsText}
      loadingText={`${t('searching')}...`}
      loading={loading}
      onChange={(event, option: T | null) => handleSelect(option)}
      onBlur={() => setQuery('')}
      ref={ref}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size="small"
          label={label}
          error={error}
          helperText={helperText}
          fullWidth={fullWidth}
          sx={{ width: fullWidth ? '100%' : 220 }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const query: string = event.target.value;
            setQuery(query);
          }}
        />
      )}
      isOptionEqualToValue={(option: T, v: T) =>
        option[idProperty] === v?.[idProperty]
      }
      disabled={disabled}
      getOptionLabel={(option) => option?.[labelProperty] ?? ''}
      forcePopupIcon={false}
    />
  );
}
